<script setup lang="ts">
import { $articleUnit, assetLink, LIQUID_MAX_AMOUNT_ML } from "@/common/util"
import type { Article } from "@/model/backend/article"
import ArticlesSidebar from "@/shop/components/ArticlesSidebar.vue"
import { useShopAppConfig } from "@/shop/composables"
import { useAddToCardNotice } from "@/shop/composables/useAddToCardNotice"
import { usePackageCombination } from "@/shop/composables/usePackageCombination"
import { useShopDialogs } from "@/shop/composables/useShopDialogs"
import { useCartStore } from "@/shop/store/useCartStore"
import { useTooManyOrdersWarningStore } from "@/shop/store/useTooManyOrdersWarning"
import { computed, onMounted, onUnmounted, ref, watch } from "vue"
import { ImageZoom } from "./image-zoom"
import ArticlePageTerpeneInfoButton from "@/shop/pages/article/ArticlePageTerpeneInfoButton.vue"
import ArticlePageHelpButton from "./ArticlePageHelpButton.vue"
import TymcTamcExplanation from "./TymcTamcExplanation.vue"
import CustomersOnlyInfo from "@/shop/components/CustomersOnlyInfo.vue"
import { $currency, useHttpClient } from "@/vf"
import { $articleType, articlePlaceholderImage } from "@/shop/util"
import { isFlowerArticleType } from "@/model/flower-types"
import { useArticleAmountSelect } from "@/shop/composables/useArticleAmountSelect"

const props = defineProps<{
    id?: string
    urlId?: string
    slug?: string
}>()

const { appConfig } = useShopAppConfig()
const dialogs = useShopDialogs()

// routes beforeEnter guard ensures that this is always defined
const mainArticle = appConfig.value.articles.find(i => (props.id ? i.id == props.id : i.urlId == props.urlId))!
const article = ref(mainArticle.isVariationArticle ? mainArticle.variations[0].childArticle : mainArticle)

/*─────────────────────────────────────┐
│  Cart handling                       │
└─────────────────────────────────────*/
const cartStore = useCartStore()
const tooManyOrdersWarning = useTooManyOrdersWarningStore()
const addToCartNotice = useAddToCardNotice()
const addedSuccessful = ref<any>(false)

// This changes the amount for essence from bottles to milliliter
const maxOrderableAmountForArticle = computed(() => {
    return article.value.type === "essence" ? LIQUID_MAX_AMOUNT_ML : maxSelectableAmount.value
})

async function addToCart(): Promise<void> {
    if (amount.value == 0) {
        missingAmount.value = true
        return
    }

    missingAmount.value = false

    // Check before alreadyInCartNotice() so an exceeded limit only shows the relevant alert, not both notices.
    const currentCartAmount = cartStore.getCartItemForArticle(article.value)?.amount ?? 0
    if (wouldExceedArticleAmountLimit(currentCartAmount, amount.value)) {
        await exeededArticleAmountLimitNotice()
        return
    }

    if (!(await alreadyInCartNotice(article.value))) {
        // article is already in cart and user decided to not add more
        return
    }

    if (await tooManyOrdersWarning.noWarningOrDoAnyways()) {
        // Check again before adding because the cart may have changed while the preceding notices were open.
        const latestCartAmount = cartStore.getCartItemForArticle(article.value)?.amount ?? 0
        if (wouldExceedArticleAmountLimit(latestCartAmount, amount.value)) {
            await exeededArticleAmountLimitNotice()
            return
        }

        cartStore.addToCart(article.value, amount.value)
        addedSuccessful.value = true
    }

    // notice to customer, that the article has been added to cart but is not reserved yet
    void addToCartNotice.open()

    setTimeout(() => (addedSuccessful.value = false), 2000)
}

async function exeededArticleAmountLimitNotice() {
    await dialogs.alert(
        "Maximale Bestellmenge erreicht",
        `Pro Bestellung ist die Menge des Artikels auf ${maxOrderableAmountForArticle.value} ${article.value.displayUnit} begrenzt. Wenn du eine größere Menge bestellen möchtest, setze dich bitte mit uns in Verbindung.`,
    )
}

async function alreadyInCartNotice(article: Article) {
    const item = cartStore.getCartItemForArticle(article)

    if (!item) {
        // article is not in cart yet
        return true
    }

    return await dialogs.confirm(
        "Artikel bereits im Warenkorb",
        `Der Artikel befindet sich bereits ${item.amount} mal in deinem Warenkorb. Möchtest du die Anzahl erhöhen?`,
    )
}

function getDryingLossColor(dryingLoss: number) {
    if (dryingLoss >= 9) {
        return "text-success"
    }
    if (dryingLoss < 9 && dryingLoss >= 6) {
        return "text-warning"
    }
    return "text-danger"
}

/*─────────────────────────────────────┐
│  Images                              │
└─────────────────────────────────────*/
const selectedImageIndex = ref<any>(0)
const imageContainer = ref<HTMLElement>()
const imageZoom = ref<any>(null)

function setupImageZoom() {
    if (!imageContainer.value) {
        return
    }
    const rect = imageContainer.value.getBoundingClientRect()
    const size = rect.width
    imageContainer.value.style.height = size + "px"
    imageZoom.value = ImageZoom(imageContainer.value, {
        img: assetLink("/uploads/media/" + mainArticle.shopImages?.[selectedImageIndex.value].imageName),
        fillContainer: false,
        width: rect.width,
        height: size,
        alt: "Artikelbild",
    })
}

function destroyImageZoom() {
    if (imageZoom.value) {
        imageZoom.value.kill()
    }
}

function selectImage(index: number) {
    selectedImageIndex.value = index
    destroyImageZoom()
    setupImageZoom()
}

onMounted(() => setupImageZoom())
onUnmounted(() => destroyImageZoom())

/*─────────────────────────────────────┐
│  Related articles                    │
└─────────────────────────────────────*/
type ArticleWithRelatedTypes = Article & {
    readymadeArticleId?: string
    milledArticleId?: string
    smallsArticleId?: string
}

const getRelatedArticleUrl = (type: "readymade" | "milled" | "smalls") => {
    const relatedId = (article.value as ArticleWithRelatedTypes)[`${type}ArticleId`]

    if (!relatedId) {
        return null
    }

    const related = appConfig.value.articles.find(a => a.id === relatedId)
    if (!related) {
        return null
    }

    return {
        name: "article",
        params: { urlId: related.urlId, slug: related.slug },
    }
}

const readymadeArticleUrl = computed(() => getRelatedArticleUrl("readymade"))
const milledArticleUrl = computed(() => getRelatedArticleUrl("milled"))
const smallsArticleUrl = computed(() => getRelatedArticleUrl("smalls"))

/*─────────────────────────────────────┐
│  Amount selection                    │
└─────────────────────────────────────*/
const { amountSelectOptions, amountSelectOptionLabel, maxSelectableAmount, wouldExceedArticleAmountLimit } =
    useArticleAmountSelect(article)

// Start at the first selectable amount: 5 g for cannabis, the smallest reachable Gebinde total for the
// prepared types (5 g readymade, 10 ml xtracts), 1 for everything else. Falls back to 0, which addToCart()
// reports as a missing amount — the only way to get there is an article without any selectable amount.
const amount = ref<number>(amountSelectOptions.value[0] ?? 0)
const missingAmount = ref<boolean>(false)

// Total weight for package combination calculation
const totalWeight = computed(() => amount.value)

// Use composable for package combination logic
const { packageCombinationText } = usePackageCombination(article, totalWeight)

const lowStockRemainingLabel = computed(() => {
    const remainingStockRaw = article.value.lowStockRemainingAmount
    const remainingStock = Number(remainingStockRaw)
    if (remainingStockRaw == null || !Number.isFinite(remainingStock)) {
        return null
    }

    return remainingStock.toLocaleString("de-DE", { maximumFractionDigits: 2 })
})

watch(
    amountSelectOptions,
    selectableAmounts => {
        if (selectableAmounts.length === 0) {
            return
        }

        if (!selectableAmounts.includes(amount.value)) {
            amount.value = selectableAmounts[0]
        }
    },
    { immediate: true },
)

onMounted(() => {
    window.scrollTo(0, 0)
})

/*─────────────────────────────────────┐
│  Tracking                            │
└─────────────────────────────────────*/
const http = useHttpClient()
// watch article changes because variations can change the displayed article
watch(
    () => article.value.id,
    () => void http.post(`/article-view/${article.value.id}`),
    { immediate: true },
)
</script>

<template>
    <template v-if="appConfig.articlesRequireLogin">
        <CustomersOnlyInfo />
    </template>
    <div v-else class="container">
        <div class="row">
            <div class="col-md-3 col-xl-2 d-md-block d-none cann-sidebar">
                <ArticlesSidebar />
            </div>
            <div class="col-md-9 col-xl-10 cann-article">
                <div class="breadcrumb mt-3">
                    <RouterLink class="breadcrumb-item" :to="{ name: 'home' }">Home</RouterLink>
                    <RouterLink class="breadcrumb-item" :to="{ name: 'articles' }">Sortiment</RouterLink>
                    <div class="breadcrumb-item">{{ $articleType(mainArticle.type!) }}</div>
                    <div class="breadcrumb-item">{{ mainArticle.publicNameSecondary }}</div>
                </div>

                <div class="row mt-5">
                    <div class="col-md-5">
                        <div v-if="(mainArticle.shopImages?.length ?? 0) == 0" style="position: relative">
                            <img
                                :src="articlePlaceholderImage(mainArticle.type)"
                                class="img-fluid"
                                alt="kein Produktbild"
                            />
                            <img
                                v-if="article.sealOfTransparency && !article.isPreparedType"
                                src="@/shop/assets/seal-of-transparency.png"
                                class="seal-of-transparency"
                                alt="seal of transparency"
                            />
                        </div>

                        <div v-if="(mainArticle.shopImages?.length ?? 0) > 0" class="article-main-image">
                            <div style="position: relative">
                                <div ref="imageContainer"></div>
                                <img
                                    v-if="article.sealOfTransparency && !article.isPreparedType"
                                    src="@/shop/assets/seal-of-transparency.png"
                                    class="seal-of-transparency"
                                    alt="seal of transparency"
                                />
                            </div>
                            <div v-if="['cannabis', 'essence'].includes(article.type!)" class="mx-1">
                                <template v-if="mainArticle.shopImages?.[selectedImageIndex]?.isBatchImage">
                                    Bild der aktuellen Charge
                                </template>
                                <template v-else>Beispielabbildung einer anderen Charge</template>
                            </div>
                            <!--                            <img-->
                            <!--                                src.bind="app.assetLink('/uploads/media/' + article.images[selectedImageIndex].imageName)"-->
                            <!--                                alt=""-->
                            <!--                                if.bind="article.imageName" class="img-fluid">-->
                        </div>

                        <div v-if="(mainArticle.shopImages?.length ?? 0) > 1" class="article-images">
                            <div
                                v-for="(image, $index) in mainArticle.shopImages"
                                :key="image.id"
                                class="article-image"
                                :class="{ active: $index === selectedImageIndex }"
                                @click="selectImage($index)"
                            >
                                <img
                                    class="img-fluid"
                                    :src="assetLink('/uploads/media/' + image.imageName)"
                                    alt="Artikelbild"
                                />
                            </div>
                        </div>

                        <div
                            v-if="article.isPreparedType && article.sealOfTransparency"
                            class="text-center mt-4"
                            aria-label="Seal of Transparency"
                            style="font-size: 1.25em"
                        >
                            <img
                                class="mr-1"
                                src="../../assets/seal-of-transparency-icon-32.png"
                                style="width: 1.25em; height: 1.25em"
                                alt=""
                            />
                            Seal of Transparency
                        </div>

                        <RouterLink class="text-muted mt-5 d-inline-block" :to="{ name: 'articles' }">
                            <i class="fa fa-fw fa-chevron-left"></i>
                            Zurück zur Übersicht
                        </RouterLink>
                    </div>
                    <div class="col-md-7">
                        <h2>{{ mainArticle.publicNamePrimary }}</h2>
                        <h1>{{ mainArticle.publicNameSecondary }}</h1>

                        <p v-if="mainArticle.text" class="mb-3 mt-3" v-html="mainArticle.text"></p>

                        <div class="related-articles-links mb-3">
                            <div v-if="readymadeArticleUrl && article.type !== 'readymade'" class="mb-3">
                                <RouterLink :to="readymadeArticleUrl">
                                    Doch lieber ganze Blüten? Hier geht’s zum passenden CANN24-Readymade-Produkt.
                                </RouterLink>
                            </div>

                            <div v-if="milledArticleUrl && article.type !== 'milled'" class="mb-3">
                                <RouterLink :to="milledArticleUrl">
                                    Lieber sparen und schon gemahlen? Hier geht’s zum passenden CANN24-Milled-Produkt.
                                </RouterLink>
                            </div>

                            <div v-if="smallsArticleUrl && article.type !== 'smalls'" class="mb-3">
                                <RouterLink :to="smallsArticleUrl">
                                    Lieber sparen mit kleinen Blüten? Hier geht’s zum passenden CANN24-Smalls-Produkt.
                                </RouterLink>
                            </div>
                        </div>

                        <div class="row">
                            <section aria-label="Produktinformationen" class="col-md-6">
                                <div v-if="article.latestBatch?.thc">
                                    THC: {{ article.latestBatch?.thc }} {{ article.potencyUnit }}*
                                </div>
                                <div v-if="article.latestBatch?.cbd">
                                    CBD: {{ article.latestBatch?.cbd }} {{ article.potencyUnit }}*
                                </div>
                                <div v-if="article.latestBatch?.tymc">
                                    TYMC: {{ article.latestBatch?.tymc.replace("<", "≤") }} KBE/g*
                                    <ArticlePageHelpButton aria-label="Erklärung von TYMC und TAMC">
                                        <TymcTamcExplanation />
                                    </ArticlePageHelpButton>
                                </div>
                                <div v-if="article.latestBatch?.tamc">
                                    TAMC: {{ article.latestBatch?.tamc.replace("<", "≤") }} KBE/g*
                                    <ArticlePageHelpButton aria-label="Erklärung von TYMC und TAMC">
                                        <TymcTamcExplanation />
                                    </ArticlePageHelpButton>
                                </div>
                                <div v-if="isFlowerArticleType(article.type) && article.latestBatch?.dryingLoss">
                                    Trocknungsverlust:
                                    <span :class="getDryingLossColor(parseFloat(article.latestBatch?.dryingLoss))">
                                        {{ article.latestBatch?.dryingLoss }} %</span
                                    ><span class="mr-1">*</span>
                                    <ArticlePageHelpButton aria-label="Erklärung von Trocknungsverlust">
                                        Der Trocknungsverlust gibt an wie viel Masse bis zur vollständigen Trocknung
                                        noch verloren gehen kann. Temperatur und Druck während der Trocknung werden so
                                        gewählt, dass diese knapp über dem Siedepunkt von Wasser liegen. Flüchtige
                                        Substanzen mit einem niedrigeren Siedepunkt als Wasser werden also auch erfasst.
                                        Dennoch liefert der Wert einen Hinweis auf die Restfeuchte. Je geringer der
                                        ausgewiesene Trocknungsverlust, desto trockener ist das Produkt bereits. Bei
                                        höheren Werten ist das Produkt feuchter. Mehr als 12% Trocknungsverlust dürfen
                                        zur Freigabe nicht erreicht werden.
                                    </ArticlePageHelpButton>
                                </div>
                                <div v-if="article.brand">Marke: {{ article.brand.name }}</div>
                                <div v-if="isFlowerArticleType(article.type) && article.strain">
                                    Strain: {{ article.strain }}
                                </div>
                                <div v-if="article.latestBatch?.pzn">PZN: {{ article.latestBatch?.pzn }}*</div>
                            </section>

                            <section aria-label="Genetik, Herkunft und Verfügbarkeit" class="col-md-6">
                                <div v-if="isFlowerArticleType(article.type)" class="d-flex align-items-center">
                                    <img
                                        class="mr-1"
                                        src="@/shop/assets/cannabis.svg"
                                        style="width: 1em; height: 1em"
                                        alt=""
                                    />
                                    Genetik: {{ article.species }}
                                </div>
                                <div
                                    v-if="
                                        ['cannabis', 'essence'].includes(article.type!) && article.packageAmount !== 1
                                    "
                                    class="d-flex align-items-center"
                                >
                                    <img
                                        class="mr-1"
                                        src="@/shop/assets/bundle.svg"
                                        style="width: 1em; height: 1em"
                                        alt=""
                                    />
                                    Gebinde: {{ article.packageAmount }} {{ article.unit }}
                                </div>
                                <div
                                    v-if="
                                        article.isPreparedType &&
                                        article.availableBatchPreparationPackageAmounts?.length
                                    "
                                    class="d-flex align-items-center"
                                >
                                    <img
                                        class="mr-1"
                                        src="@/shop/assets/bundle.svg"
                                        style="width: 1em; height: 1em"
                                        alt=""
                                    />
                                    Gebinde:
                                    {{
                                        article.availableBatchPreparationPackageAmounts
                                            .map(x => `${x} ${article.batchPreparationPackageDisplayUnit}`)
                                            .join(", ")
                                    }}
                                </div>
                                <div v-if="article.countryOfOrigin" class="d-flex align-items-center">
                                    <i class="fas fa-earth-europe mr-1"></i>
                                    Herkunftsland: {{ article.countryOfOrigin }}
                                </div>
                                <div class="d-flex align-items-center text-nowrap">
                                    <div
                                        class="mr-1"
                                        style="width: 1rem; height: 1rem; border-radius: 50%"
                                        :class="(article.inStock ? 'bg-success' : 'bg-danger') + ' d-inline-block'"
                                    ></div>
                                    {{ article.inStock ? "Lieferbar" : "Nicht lieferbar" }}
                                </div>
                                <div class="d-flex align-items-center">
                                    <div v-if="!article.inStock" class="font-weight-bold text-nowrap>">
                                        {{ article.notInStockText || "Liefertermin unbekannt" }}
                                    </div>
                                </div>
                                <div
                                    v-if="article.sealOfTransparency && !article.isPreparedType"
                                    class="d-flex align-items-center"
                                >
                                    <img
                                        class="mr-1"
                                        src="../../assets/seal-of-transparency-icon-32.png"
                                        style="width: 1em; height: 1em"
                                        alt=""
                                    />
                                    Seal of Transparency
                                </div>
                            </section>
                        </div>

                        <i class="small text-muted"
                            >* Werte der aktuellen Charge.
                            <span v-if="article.latestBatch?.thcMeasurement">
                                Der THC-Wert wurde {{ article.latestBatch?.thcMeasurement }}.
                            </span>
                        </i>

                        <div v-if="article.isSellOffArticle" aria-label="Ausverkauf" class="mt-1">
                            Dieses Produkt verlässt unser Lagersortiment!
                        </div>

                        <section
                            v-if="appConfig.user && isFlowerArticleType(article.type)"
                            class="mt-3"
                            aria-label="Zusätzliche Informationen"
                        >
                            <div v-if="article.crossing"><b>Kreuzung:</b> {{ article.crossing }}</div>

                            <div v-if="article.cultivationFacility">
                                <b>Anbaubetrieb:</b> {{ article.cultivationFacility }}
                            </div>

                            <div v-if="article.cultivationMethod"><b>Anbauart:</b> {{ article.cultivationMethod }}</div>

                            <div v-if="article.irradiationMethod">
                                <b>Bestrahlungsmethode:</b> {{ article.irradiationMethod }}
                            </div>

                            <div v-if="article.latestBatch?.releaseSpecification">
                                <b>Freigabespezifikation:</b>
                                {{ article.latestBatch.releaseSpecification }}*
                            </div>

                            <div v-if="article.terpeneProfile">
                                <b>Die dominanten Terpene sind:</b>
                                <ul>
                                    <li v-for="(terpene, index) in article.terpeneProfile" :key="index">
                                        {{ terpene }}
                                        <ArticlePageTerpeneInfoButton
                                            v-if="
                                                appConfig.terpeneDescriptions[terpene].occurrence ||
                                                appConfig.terpeneDescriptions[terpene].description
                                            "
                                            :terpene="appConfig.terpeneDescriptions[terpene]"
                                        />
                                    </li>
                                </ul>
                            </div>
                            <div v-else>Es liegen keine Angaben zum Terpenprofil vor.</div>
                        </section>

                        <!--                        <div class="d-flex my-3" if.bind="article.price">-->
                        <!--                            <div class="article-price mr-4 my-auto">-->
                        <!--                                ${article.price | currency}-->
                        <!--                            </div>-->
                        <!--                            <div class="font-italic text-muted my-auto">-->
                        <!--                                pro ${article.unit | articleUnit} inkl. gesetzlicher MwSt. zzgl. Versand-->
                        <!--                            </div>-->
                        <!--                        </div>-->
                        <!--                        -->

                        <div
                            v-if="article.showLowStockWarning && lowStockRemainingLabel"
                            class="alert alert-warning mt-5"
                        >
                            <b>Achtung:</b>
                            Nur noch {{ lowStockRemainingLabel }} {{ article.unit }} auf Lager.
                        </div>

                        <div class="mt-5"></div>

                        <div v-if="mainArticle.isVariationArticle" class="row">
                            <div class="col-md-4">
                                <b>{{ mainArticle.variationLabel }}</b>
                            </div>
                            <div class="col-md-4">
                                <select v-model="article" class="form-control custom-select">
                                    <option
                                        v-for="variation in mainArticle.variations"
                                        :key="variation.id"
                                        :value="variation.childArticle"
                                    >
                                        {{ variation.name }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <section
                            v-if="article.allow && article.inStock && article.isPreparedType && article.packagingType"
                            aria-label="Packmittel"
                        >
                            <div class="py-2 row align-items-center">
                                <div class="col-md-4">
                                    <b>Packmittel</b>
                                </div>

                                <div class="col-md-8 col-7 text-left mt-2 mt-md-0">
                                    <div class="article-packaging">
                                        {{ article.packagingType }}
                                    </div>
                                </div>
                            </div>
                        </section>
                        <section v-if="article.allow && article.inStock" aria-label="Menge ändern">
                            <div class="py-2 row align-items-center">
                                <div class="col-md-4">
                                    <b>Menge</b>
                                </div>
                                <div class="col-md-5 col-7 mt-2 mt-md-0">
                                    <select
                                        v-model="amount"
                                        class="form-control custom-select"
                                        aria-label="Mengenauswahl"
                                    >
                                        <option
                                            v-for="selectableAmount in amountSelectOptions"
                                            :key="selectableAmount"
                                            :value="selectableAmount"
                                        >
                                            {{ amountSelectOptionLabel(selectableAmount) }}
                                        </option>
                                    </select>
                                </div>
                                <div class="col-md-3 col-5 text-right mt-2 mt-md-0">
                                    <div class="article-price">
                                        {{ $currency(article.price * amount) }}
                                    </div>
                                </div>
                            </div>
                        </section>
                        <section
                            v-if="article.allow && article.inStock && article.isPreparedType && packageCombinationText"
                            aria-label="Gebinde-Information"
                        >
                            <div class="alert alert-info mt-2">
                                <i class="fa fa-info-circle mr-1"></i>
                                <span>
                                    Deine Bestellung wird voraussichtlich mit folgenden Abpackungen zusammengestellt:
                                    <strong>{{ packageCombinationText }}</strong>
                                </span>
                            </div>
                        </section>

                        <section
                            v-if="article.price && article.inStock"
                            class="text-muted small"
                            aria-label="Preis pro Einheit"
                        >
                            <br />
                            {{ $currency(article.normalizedPricePerDisplayUnit!) }} /
                            {{ $articleUnit(article.normalizedPackageUnit!) }}
                            inkl. MwSt. zzgl. Versand
                        </section>

                        <div class="mb-5"></div>

                        <div v-if="article.inStock" class="d-flex align-items-center">
                            <button v-if="article.allow" class="btn btn-primary" @click="addToCart()">
                                In den Warenkorb
                            </button>
                            <span v-show="missingAmount" class="pl-3 text-primary">Bitte wähle eine Anzahl aus!</span>
                            <span v-show="addedSuccessful" class="pl-3 text-primary">Artikel hinzugefügt!</span>
                        </div>
                    </div>

                    <div v-if="article.requiresPrescription">
                        <hr />

                        <p class="text-muted small px-3">
                            Angaben zum Strain, Wirkstoffgehalt und genetischer Zuordnung (Sativa Dominanz, Indica
                            Dominanz, Hybrid Balance) beziehen sich auf die Angaben des Herstellers und erfolgen ohne
                            Gewähr. Da es sich um ein Naturprodukt handelt, können die Werte auch innerhalb einer Charge
                            schwanken. Dieser Tatsache wurde bei Chargenfreigabe Sorge getragen. Die Bilder können auch
                            aus älteren Chargen des Produktes stammen.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.seal-of-transparency {
    position: absolute;
    width: 7.5rem;
    height: 7.5rem;
    bottom: 0.4rem;
    right: 0.4rem;
}
</style>
